import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import { BodyPrimary, H5 } from '@liveauctioneers/caterwaul-components/lib/Text/Text';
import styled from 'styled-components';

import PageTitle from '@/components/PageTitle/PageTitle';

import ACHTermsAndConditionsPageHeaderMeta from './TermsAndConditionsPageHeaderMeta';

const ACHTermsAndConditionsPage = () => {
    const mailTo = (
        <Link to="mailto:info@liveauctioneers.com">
            <span>info@liveauctioneers.com</span>
        </Link>
    );

    return (
        <ContentContainer>
            <ACHTermsAndConditionsPageHeaderMeta />
            <div>
                <PageTitle title="ACH Payment Terms and Conditions" />
                <br />
                <br />
                <H5>Introduction</H5>
                <BodyPrimary>
                    Welcome to LiveAuctioneers’ (“LiveAuctioneers”) terms and conditions pertaining to the use of ACH
                    payments and bank accounts registered with LiveAuctioneers or its agent(s) as a payment method for
                    your participation in the live auction services hosted on our website (the "ACH Payment Terms and
                    Conditions"). Please note that your use of the LiveAuctioneers’ website is also governed by our Live
                    Auction User Agreement, Privacy Policy, and any other agreement(s) to which we are parties, all as
                    may be amended, from time to time, and are incorporated into these ACH Payment Terms and Conditions.
                    If you choose to use a bank account as your payment method, you accept and agree to these ACH
                    Payment Terms and Conditions.
                </BodyPrimary>
                <ol>
                    <li>
                        <BodyPrimary>
                            <strong>Bank Account Payments</strong>
                            <br />
                            By choosing to use a bank account as your payment method, you will be able to pay the fees
                            due in connection with your participation in any auctions hosted on our website by using any
                            valid automated clearing house ("ACH") enabled bank account at a United States-based
                            financial institution. Whenever you choose to pay by ACH, you are authorizing
                            LiveAuctioneers (or its agent) to debit your bank account for the total amount of the fees
                            due. Your transaction must be payable in U.S. dollars. Notwithstanding the foregoing,
                            LiveAuctioneers, in its sole and absolute discretion, may refuse this payment option to
                            anyone, or any user, without notice, for any reason at any time. All questions relating to
                            any transactions made using your bank account by LiveAuctioneers should be initially
                            directed to us, but may also require involvement of your bank.
                        </BodyPrimary>
                    </li>
                    <li>
                        <BodyPrimary>
                            <strong>ACH Authorization</strong>
                            <br />
                            By choosing your bank account as your payment method, you agree that: (a) you have read,
                            understand, and agree to these ACH Payment Terms and Conditions, and that this agreement
                            constitutes a "writing signed by you" under any applicable law or regulation, (b) you
                            consent to the electronic delivery of the disclosures contained in these ACH Payment Terms
                            and Conditions, (c) you authorize LiveAuctioneers (or its agent) to make any inquiries we
                            consider necessary to validate any dispute involving your payment, which may include
                            ordering a credit report and performing other credit checks or verifying the information you
                            provide against third party databases, and (d) you authorize LiveAuctioneers (or its agent)
                            to initiate one or more ACH debit entries (withdrawals), and you authorize the financial
                            institution that holds your bank account to deduct such payments, in the amounts and
                            frequency designated in your then-current payment plan, if any.
                        </BodyPrimary>
                    </li>
                    <li>
                        <BodyPrimary>
                            <strong>Transaction Errors</strong>
                            <br />
                            If you believe that any payment transaction initiated by LiveAuctioneers (or its agent) with
                            respect to your bank account is erroneous, or if you need more information about any such
                            transaction, you should contact us as soon as possible at {mailTo}. Notify us immediately if
                            you believe the password associated with your account has been lost or stolen, or if someone
                            has attempted (or may attempt) to make a transfer from your bank account without your
                            permission. We reserve the right, in our sole and absolute discretion, to cancel the ability
                            to pay by bank account for any reason at any time.
                        </BodyPrimary>
                    </li>
                    <li>
                        <BodyPrimary>
                            <strong>Your Liability for Unauthorized Transactions</strong>
                            <br />
                            Federal law limits your liability for any fraudulent, erroneous, or unauthorized transaction
                            from your bank account based on how quickly you report it to your financial institution. As
                            a general rule, you should report any fraudulent, erroneous, or unauthorized transactions to
                            your bank within sixty (60) days after the questionable transaction FIRST appeared on your
                            bank account statement. You should contact your bank for more information about the policies
                            and procedures that apply to your account and any unauthorized transactions, including any
                            limits on your liability.
                        </BodyPrimary>
                    </li>
                    <li>
                        <BodyPrimary>
                            <strong>Payment Authorizations and Revocation</strong>
                            <br />
                            You authorize LiveAuctioneers and its designated payment processors to store your designated
                            bank account information and other related information. You authorize LiveAuctioneers to
                            automatically debit all applicable charges from your designated bank account via ACH debit
                            on the date such charges become due, and you agree to follow NACHA rules applicable to ACH
                            transactions. This authorization to initiate ACH debit transactions will remain in full
                            force and effect until LiveAuctioneers has received written notice revoking such
                            authorization from you by email at {mailTo}, which must be, at minimum, ten (10) days in
                            advance of the date that the requested revocation is to take effect. Because these are
                            electronic transactions, these funds may be withdrawn from your designated bank account
                            immediately. In the case of an ACH debit transaction that is rejected for insufficient
                            funds, you understand and agree that LiveAuctioneers may, in its sole and absolute
                            discretion, attempt again to process the debit in the amount due within thirty (30) days of
                            the date such transaction is rejected due to insufficient funds, as permitted by applicable
                            law. You certify that you are an authorized user of the designated bank account and will not
                            dispute these scheduled transactions with such bank, so long as the transactions correspond
                            to the amount you owe. You must notify LiveAuctioneers promptly, in writing, if you dispute
                            any portion of any fees paid or payable by you under these ACH Payment Terms and Conditions,
                            or any other agreement(s) to which we are parties. If you do not provide LiveAuctioneers
                            with such prompt written notice of your fee dispute, you will not be entitled to dispute any
                            fees that were paid or are payable by you. If you fail to pay any amounts then due,
                            LiveAuctioneers may suspend your user account(s) without prior notice. LiveAuctioneers shall
                            have no liability for any damages, liabilities, losses, costs, or expenses (including any
                            loss of data or profits) or any other consequences that you or any user may incur in
                            connection with the suspension of any accounts pursuant to this section.
                        </BodyPrimary>
                    </li>
                    <li>
                        <BodyPrimary>
                            <strong>Electronic Delivery of Future Disclosures</strong>
                            <br />
                            You agree to accept all disclosures and other communications between you and LiveAuctioneers
                            on this website or at the primary e-mail address associated with your LiveAuctioneers
                            account. You should print and retain a copy of all such disclosures and communications.
                        </BodyPrimary>
                    </li>
                    <li>
                        <BodyPrimary>
                            <strong>Agreement Changes</strong>
                            <br />
                            We may, in our sole and absolute discretion, change these ACH Payment Terms and Conditions
                            at any time, without notice to you. If any change is found to be invalid, void, or, for any
                            reason, unenforceable, that change is severable and does not affect the validity and
                            enforceability of any other changes or the remainder of these ACH Payment Terms and
                            Conditions. We reserve the right to subcontract any of our rights or obligations under these
                            ACH Payment Terms and Conditions.
                            <br />
                            <br />
                            <strong>
                                YOUR CONTINUED USE OF YOUR BANK ACCOUNT AS A PAYMENT METHOD ON THE LIVEAUCTIONEERS
                                WEBSITE AFTER WE CHANGE THESE TERMS AND CONDITIONS CONSTITUTES YOUR ACCEPTANCE OF ANY
                                SUCH CHANGES.
                            </strong>
                        </BodyPrimary>
                    </li>
                </ol>
            </div>
        </ContentContainer>
    );
};

export default ACHTermsAndConditionsPage;

const ContentContainer = styled.div`
    max-width: ${({ theme }) => theme.breakpoints.pageWidth};
    margin: 0 auto;
    padding: 0 20px 30px;

    h2 {
        padding-top: 30px;
        padding-bottom: 18px;
    }
`;
